import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ChessBoardView from '@/components/chess/ChessBoardView';
import { Button } from '@/components/ui/button';
import { Clock, Sparkles, Check, X, ArrowRight } from 'lucide-react';

// Runner del ALUMNO para actividades type === 'multiple_choice'.
//
// CONTRATO (idéntico a QuizPuzzleRunner / QuizCaptureRunner / QuizPlayComputerRunner):
//   { activity, position, totalActivities, quizTitle,
//     maxPoints, maxAttempts, initialAttemptsUsed,
//     onCompleted, onAttemptFailed }
//
// REGLAS:
//   · El alumno NO mueve piezas: el tablero es SOLO visual (arePiecesDraggable=false).
//   · Opciones desde activity.options; respuesta correcta desde activity.correct_index.
//   · Puntuación consistente con los demás runners del Quiz: puntos completos
//     (maxPoints = quiz.max_points_per_activity) al acertar, 0 al agotar intentos.
//   · Intentos: respeta maxAttempts. Fallo con intentos restantes → onAttemptFailed
//     (persistencia parcial) y permite reintentar. Fallo en el último intento o
//     acierto → onCompleted (una sola vez).
//   · Tiempo: si activity.time_seconds > 0 se muestra cuenta atrás local; al llegar
//     a 0 se finaliza como fallo. NO se toca el temporizador global del Quiz.
//   · La persistencia va por onCompleted → QuizStart → POST /quiz-assignments/{aid}/progress.

function fmtTime(sec) {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

const OPTION_LETTERS = ['A', 'B', 'C', 'D', 'E'];

export default function QuizMultipleChoiceRunner({
  activity, position, totalActivities, quizTitle,
  maxPoints = 30, maxAttempts = 3, initialAttemptsUsed = 0,
  onCompleted, onAttemptFailed,
}) {
  const options = useMemo(() => (Array.isArray(activity?.options) ? activity.options.filter(o => String(o).trim() !== '') : []), [activity]);
  const correctIndex = Number(activity?.correct_index) || 0;
  const timeLimit = Number.isFinite(activity?.time_seconds) && activity.time_seconds > 0 ? Number(activity.time_seconds) : 0;

  const [selected, setSelected] = useState(null);
  const [phase, setPhase] = useState('answering'); // 'answering' | 'feedback'
  const [lastCorrect, setLastCorrect] = useState(false);
  const [attemptsUsed, setAttemptsUsed] = useState(Number(initialAttemptsUsed) || 0);
  const [done, setDone] = useState(false);          // sin más intentos o acertó
  const [timedOut, setTimedOut] = useState(false);
  const [remaining, setRemaining] = useState(timeLimit || 0);
  // Layout-only: ancho del tablero adaptado al contenedor (evita overflow en
  // mobile). Tope de 340 → el desktop mantiene el tamaño actual.
  const [boardPx, setBoardPx] = useState(340);
  const boardWrapRef = useRef(null);

  const attemptsRef = useRef(Number(initialAttemptsUsed) || 0);
  const selectedRef = useRef(null);
  const startedMsRef = useRef(Date.now());
  const finishedRef = useRef(false);
  const doneRef = useRef(false);

  // Reset al cambiar de actividad.
  useEffect(() => {
    setSelected(null); setPhase('answering'); setLastCorrect(false);
    setAttemptsUsed(Number(initialAttemptsUsed) || 0); setDone(false); setTimedOut(false);
    setRemaining(timeLimit || 0);
    attemptsRef.current = Number(initialAttemptsUsed) || 0;
    selectedRef.current = null;
    startedMsRef.current = Date.now();
    finishedRef.current = false; doneRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity?.id, activity?.source_activity_id]);

  // Layout-only: mide el ancho disponible del contenedor del tablero y ajusta
  // boardPx (mínimo 200, máximo 340). Puramente visual, no toca la lógica.
  useEffect(() => {
    const el = boardWrapRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth || 0;
      if (w > 0) setBoardPx(Math.max(200, Math.min(340, Math.floor(w))));
    };
    measure();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    window.addEventListener('resize', measure);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', measure); };
  }, []);

  const finalize = useCallback((success) => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    const elapsed = Math.max(0, Math.floor((Date.now() - startedMsRef.current) / 1000));
    try {
      onCompleted?.({
        activity_id: activity?.source_activity_id || activity?.id,
        position,
        attempts_used: attemptsRef.current,
        points_earned: success ? Math.max(0, Math.round((Number(maxPoints) || 0) * (maxAttempts - (attemptsRef.current - 1)) / Math.max(1, maxAttempts))) : 0,
        selected_index: Number.isInteger(selectedRef.current) ? selectedRef.current : -1,
        elapsed_seconds: elapsed,
        success,
        completed: true,
      });
    } catch { /* el padre no debe romper el runner */ }
  }, [activity, position, maxPoints, maxAttempts, onCompleted]);

  // Cuenta atrás local (solo si time_seconds > 0).
  useEffect(() => {
    if (timeLimit <= 0 || doneRef.current) return;
    const iv = setInterval(() => {
      const left = Math.max(0, timeLimit - Math.floor((Date.now() - startedMsRef.current) / 1000));
      setRemaining(left);
      if (left === 0 && !doneRef.current) {
        doneRef.current = true;
        setTimedOut(true); setLastCorrect(false); setDone(true); setPhase('feedback');
        finalize(false);
      }
    }, 500);
    return () => clearInterval(iv);
  }, [timeLimit, finalize]);

  const submit = () => {
    if (phase !== 'answering' || selected === null || doneRef.current) return;
    const isCorrect = selected === correctIndex;
    const newAttempts = attemptsRef.current + 1;
    attemptsRef.current = newAttempts;
    setAttemptsUsed(newAttempts);
    setLastCorrect(isCorrect);
    setPhase('feedback');
    const elapsed = Math.max(0, Math.floor((Date.now() - startedMsRef.current) / 1000));
    if (isCorrect) {
      doneRef.current = true; setDone(true);
    } else if (newAttempts >= maxAttempts) {
      doneRef.current = true; setDone(true);
    } else {
      // Fallo con intentos restantes → persistencia parcial, permite reintentar.
      try { onAttemptFailed?.({ activity_id: activity?.source_activity_id || activity?.id, position, attempts_used: newAttempts, selected_index: Number.isInteger(selectedRef.current) ? selectedRef.current : -1, elapsed_seconds: elapsed }); } catch { /* silencioso */ }
    }
  };

  const tryAgain = () => {
    if (doneRef.current) return;
    setSelected(null); selectedRef.current = null; setPhase('answering'); setLastCorrect(false);
  };

  if (!activity) return null;
  const attemptsLeft = Math.max(0, maxAttempts - attemptsUsed);
  const pct = totalActivities > 0 ? Math.round((position / totalActivities) * 100) : 0;
  const boardFen = activity.fen || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

  if (options.length === 0) {
    return (
      <div data-testid="qmc-invalid" className="min-h-[calc(100vh-4rem)] grid place-items-center p-6 bg-gradient-to-b from-indigo-50/40 via-white to-white">
        <div className="max-w-lg text-center rounded-2xl bg-white border border-slate-200 p-6 shadow-sm">
          <div className="text-5xl mb-3">♟️</div>
          <div className="font-display font-black text-xl text-slate-900">Esta actividad no tiene opciones de respuesta.</div>
          <div className="text-sm text-slate-600 mt-2">Avísale a tu profesor para que la revise.</div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="qmc-runner" className="min-h-[calc(100vh-4rem)] bg-gradient-to-b from-indigo-50/40 via-white to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        {/* Header (mismo estilo que los otros runners) */}
        <div className="mb-4">
          <div data-testid="qmc-title" className="font-display font-black text-xl sm:text-2xl text-slate-900 truncate">🧠 {quizTitle}</div>
          <div className="flex items-center justify-between mt-1 gap-3">
            <div data-testid="qmc-progress-text" className="text-xs sm:text-sm text-slate-600 font-semibold">Actividad {position + 1} de {totalActivities}</div>
            <div className="flex items-center gap-2">
              {timeLimit > 0 && (
                <div data-testid="qmc-countdown" className={`inline-flex items-center gap-1 text-xs sm:text-sm font-mono ${remaining <= 10 ? 'text-rose-600' : 'text-slate-500'}`}>
                  <Clock className="w-3.5 h-3.5" /> {fmtTime(remaining)}
                </div>
              )}
              <div data-testid="qmc-attempt-badge" className="text-[11px] text-slate-500 font-semibold whitespace-nowrap">
                <Sparkles className="w-3 h-3 inline mr-0.5" /> Intento {Math.min(attemptsUsed + (done ? 0 : 1), maxAttempts)} de {maxAttempts}
              </div>
            </div>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden mt-2">
            <div data-testid="qmc-progress-bar" className="h-full bg-gradient-to-r from-indigo-500 to-fuchsia-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          {/* Tablero visual (solo lectura) */}
          <div data-testid="qmc-board" className="rounded-2xl bg-white border border-slate-200 p-3 shadow-sm">
            <div ref={boardWrapRef} className="w-full grid place-items-center">
              <ChessBoardView fen={boardFen} boardWidth={boardPx} arePiecesDraggable={false} />
            </div>
          </div>

          {/* Pregunta + opciones */}
          <div className="space-y-4">
            {activity.title && <div className="font-display font-black text-lg text-slate-900">{activity.title}</div>}
            <div data-testid="qmc-question" className="text-base sm:text-lg text-slate-800 font-semibold">
              {activity.question || '¿Qué respuesta es la correcta?'}
            </div>

            <div className="space-y-2" data-testid="qmc-options">
              {options.map((op, i) => {
                const isSel = selected === i;
                const showCorrect = phase === 'feedback' && i === correctIndex;
                const showWrongPick = phase === 'feedback' && isSel && i !== correctIndex;
                return (
                  <button
                    key={i}
                    type="button"
                    data-testid={`qmc-option-${i}`}
                    disabled={phase === 'feedback' || done}
                    onClick={() => { setSelected(i); selectedRef.current = i; }}
                    className={`w-full text-left flex items-center gap-3 rounded-xl border px-4 py-3 transition-all
                      ${showCorrect ? 'border-emerald-500 bg-emerald-50'
                        : showWrongPick ? 'border-rose-500 bg-rose-50'
                        : isSel ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
                        : 'border-slate-200 bg-white hover:bg-slate-50'}
                      ${(phase === 'feedback' || done) ? 'cursor-default' : 'cursor-pointer'}`}
                  >
                    <span className={`grid place-items-center w-7 h-7 rounded-full text-sm font-bold shrink-0
                      ${showCorrect ? 'bg-emerald-500 text-white' : showWrongPick ? 'bg-rose-500 text-white' : isSel ? 'bg-indigo-500 text-white' : 'bg-slate-100 text-slate-600'}`}>
                      {OPTION_LETTERS[i] || i + 1}
                    </span>
                    <span className="text-sm sm:text-base text-slate-800">{op}</span>
                    {showCorrect && <Check className="w-4 h-4 text-emerald-600 ml-auto" />}
                    {showWrongPick && <X className="w-4 h-4 text-rose-600 ml-auto" />}
                  </button>
                );
              })}
            </div>

            {/* Feedback */}
            {phase === 'feedback' && (
              <div
                data-testid={lastCorrect ? 'qmc-feedback-correct' : 'qmc-feedback-wrong'}
                className={`rounded-xl px-4 py-3 text-sm font-semibold ${lastCorrect ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'}`}
              >
                {lastCorrect
                  ? <><Check className="w-4 h-4 inline mr-1" /> ¡Respuesta correcta! +{Math.max(0, Math.round((Number(maxPoints) || 0) * (maxAttempts - (attemptsUsed - 1)) / Math.max(1, maxAttempts)))} puntos</>
                  : timedOut
                    ? <><X className="w-4 h-4 inline mr-1" /> Se acabó el tiempo.</>
                    : done
                      ? <><X className="w-4 h-4 inline mr-1" /> Respuesta incorrecta. Sin intentos restantes.</>
                      : <><X className="w-4 h-4 inline mr-1" /> Respuesta incorrecta. Te quedan {attemptsLeft} intento(s).</>}
              </div>
            )}

            {/* Acciones */}
            <div className="pt-1">
              {phase === 'answering' && (
                <Button data-testid="qmc-submit" onClick={submit} disabled={selected === null}
                  className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 rounded-xl px-8">
                  Responder
                </Button>
              )}
              {phase === 'feedback' && !done && (
                <Button data-testid="qmc-try-again" onClick={tryAgain} variant="outline" className="w-full sm:w-auto rounded-xl px-8">
                  Intentar de nuevo
                </Button>
              )}
              {phase === 'feedback' && done && (
                <Button data-testid="qmc-continue" onClick={() => finalize(lastCorrect)}
                  className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 rounded-xl px-8">
                  Continuar <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
